// ============================================
// STORE DE PROYECTO (Pinia)
// ============================================
// Fuente única de verdad para el proyecto seleccionado, la lista de proyectos
// y el menú. Persiste en sessionStorage/localStorage con las MISMAS claves que
// usa el código aún no migrado (proyectSelected, proyectSelectedName, menu,
// proyectos, hb:proyectos, hb:last-project), de modo que ambas partes de la
// app ven siempre el mismo estado.
//
// Uso:
//   import { useProyectoStore } from '@/stores/proyecto';
//   const proyecto = useProyectoStore();
//   proyecto.hydrate();            // cargar desde storage
//   proyecto.proyectos             // lista
//   proyecto.seleccionar(indice);  // cambiar de proyecto (persiste todo)

import { defineStore } from 'pinia';
import { safeJsonParse } from '../utils/helpers';

// El Asistente de Configuración marca esta clave mientras configura un proyecto
// que TODAVÍA NO EXISTE (pasos 1 y 2). En ese rato "sin proyecto activo" es el
// estado correcto, no un fallo de hidratación: sin esta marca, la autoselección
// de proyecto único volvía a activar el proyecto anterior a la primera
// hidratación (Layout la ejecuta en cada `hb:session-updated`) y el asistente
// terminaba creando la caseta y los cuarteles en el proyecto equivocado.
export const MODO_PROYECTO_NUEVO_KEY = 'hb:wizard:proyecto-nuevo';

export function enModoProyectoNuevo() {
  return sessionStorage.getItem(MODO_PROYECTO_NUEVO_KEY) === '1';
}

function leerProyectosDeStorage() {
  const raw = sessionStorage.getItem('proyectos') || localStorage.getItem('hb:proyectos') || '[]';
  const parsed = safeJsonParse(raw, []);
  return Array.isArray(parsed) ? parsed : [];
}

export const useProyectoStore = defineStore('proyecto', {
  state: () => ({
    proyectos: [],
    proyectoId: '',
    proyectoNombre: '',
    menu: null
  }),

  getters: {
    hasMultiples: (state) => state.proyectos.length > 1,
    haySeleccion: (state) => Boolean(state.proyectoId)
  },

  actions: {
    /** Carga el estado desde sessionStorage/localStorage. */
    hydrate() {
      this.proyectos = leerProyectosDeStorage();
      this.proyectoId = sessionStorage.getItem('proyectSelected') || '';
      this.proyectoNombre = sessionStorage.getItem('proyectSelectedName') || '';
      this.menu = safeJsonParse(sessionStorage.getItem('menu'), null);

      // Auto-selección cuando existe un único proyecto y nada seleccionado
      if (!this.proyectoId && this.proyectos.length === 1 && !enModoProyectoNuevo()) {
        this.seleccionar(0, { redirect: false });
      }
    },

    /** Reemplaza la lista de proyectos y la persiste. */
    setProyectos(lista) {
      this.proyectos = Array.isArray(lista) ? lista : [];
      sessionStorage.setItem('proyectos', JSON.stringify(this.proyectos));
      localStorage.setItem('hb:proyectos', JSON.stringify(this.proyectos));
    },

    /**
     * Re-consulta al backend los proyectos a los que el usuario tiene acceso
     * y actualiza la lista + persistencia. Úsese tras crear/asignar un
     * proyecto para que el menú lateral lo refleje sin re-login.
     * Devuelve true si actualizó.
     */
    async refrescar() {
      const { getUserFromToken } = await import('../utils/auth');
      const { fetchWithToken } = await import('../utils/helpers');
      const usuario = getUserFromToken();
      if (!usuario?.id) return false;
      try {
        const res = await fetchWithToken(`/api/general-access-user?id=${usuario.id}`, { method: 'GET' });
        if (!res.ok) return false;
        const data = await res.json();
        const lista = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        if (!lista.length) return false;

        // La lista del endpoint es autoritativa para todos los perfiles: para
        // superadmin devuelve TODOS los proyectos activos (ver Access.js, la
        // consulta `SELECT ... FROM proyectos WHERE status = 1`), no una lista
        // parcial. Antes se UNÍA con la copia local "para no encoger la lista",
        // y eso resucitaba de forma permanente los proyectos eliminados: al
        // borrar uno y volver a crearlo con el mismo nombre, el selector mostraba
        // el nombre DUPLICADO (la fila vieja con status=3 más la nueva) hasta
        // cerrar sesión. El caso que esa unión protegía —una respuesta vacía o
        // fallida— ya lo cubre el `if (!lista.length) return false` de arriba.
        // Se deduplica por id por si el backend repitiera una fila.
        const porId = new Map(lista.map((p) => [String(p.id), p]));
        this.setProyectos([...porId.values()]);

        // El menú del proyecto activo vive en sessionStorage.menu, y el único
        // sitio que lo reescribe es seleccionar(). Sin re-aplicar la selección,
        // `proyectos` quedaba fresco pero el sidebar seguía leyendo el menú
        // viejo: los submódulos habilitados en el Centro de Accesos —y los
        // cuarteles/casetas recién creados— no aparecían hasta cerrar y volver
        // a abrir sesión (que es lo único que limpiaba el storage).
        const idxActivo = this.proyectos.findIndex((p) => String(p.id) === String(this.proyectoId));
        if (idxActivo >= 0) {
          // seleccionar() persiste menu + proyectos y emite hb:session-updated.
          this.seleccionar(idxActivo, { redirect: false });
          return true;
        }

        // Notifica al Layout/App para que reconstruyan el menú
        window.dispatchEvent(new Event('hb:session-updated'));
        return true;
      } catch {
        return false;
      }
    },

    /**
     * Selecciona un proyecto por índice de la lista. Persiste todas las
     * claves y notifica al resto de la app (hb:session-updated).
     */
    seleccionar(indice, { redirect = false } = {}) {
      const proyecto = this.proyectos[indice];
      if (!proyecto?.id) return false;

      const menu = proyecto.menu?.[0] || {};

      this.proyectoId = String(proyecto.id);
      this.proyectoNombre = proyecto.proyectoName || '';
      this.menu = menu;

      sessionStorage.setItem('proyectSelected', this.proyectoId);
      sessionStorage.setItem('proyectSelectedName', this.proyectoNombre);
      sessionStorage.setItem('menu', JSON.stringify(menu));
      sessionStorage.setItem('proyectos', JSON.stringify(this.proyectos));
      localStorage.setItem('hb:proyectos', JSON.stringify(this.proyectos));
      localStorage.setItem('hb:last-project', JSON.stringify({
        id: proyecto.id,
        name: this.proyectoNombre,
        menu
      }));

      window.dispatchEvent(new Event('hb:session-updated'));

      if (redirect) {
        // Recarga para que toda la app se reinicialice con el nuevo proyecto
        window.location.href = '/home';
      }
      return true;
    },

    /**
     * Selecciona un proyecto por su id. Es la vía que usa el Asistente de
     * Configuración: al crear un proyecto conoce su id (lo devuelve
     * save-proyect) pero no su posición en la lista, y al elegir cuál
     * configurar tampoco. Devuelve false si ese id no está en la lista
     * (proyecto ajeno o lista sin refrescar).
     */
    seleccionarPorId(id, opciones = {}) {
      const indice = this.proyectos.findIndex((p) => String(p.id) === String(id));
      if (indice < 0) return false;
      return this.seleccionar(indice, opciones);
    },

    /** Limpia el estado en memoria (la persistencia la limpia el logout). */
    clear() {
      this.proyectos = [];
      this.proyectoId = '';
      this.proyectoNombre = '';
      this.menu = null;
    }
  }
});
